import { useContext, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import type { InvoiceDetailResponse } from '../../types/invoice';
import {
    finishInvoiceUpdate,
    listInvoiceUpdates,
    subscribeInvoiceUpdateJobs,
    type InvoiceUpdateJobKind,
    type TrackedInvoiceUpdate,
} from '../../hooks/invoice/invoiceUpdateJobTracker';

const POLL_MS = 2000;
const COMPLETION_WAIT_MS = 150_000;

interface JobStatusResponse {
    jobId: string;
    invoiceId: string;
    kind: InvoiceUpdateJobKind;
    status: 'processing' | 'succeeded' | 'failed';
    invoiceStatus?: string;
    nexRefreshWarning?: { title?: string; detail?: string; httpStatus?: number };
    error?: { message?: string; title?: string; detail?: string; timedOut?: boolean };
}

const processingTitle = (kind: InvoiceUpdateJobKind): string => {
    if (kind === 'submit') return 'Submitting invoice';
    if (kind === 'refresh') return 'Refreshing Nexsyis data';
    return 'Updating invoice';
};

const updateCompletedAtMs = (value: string | Date | null | undefined): number | null => {
    if (value == null || value === '') return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
};

/**
 * Keeps submit and Nexsyis refresh alive after the user leaves the invoice.
 * A gateway timeout is "still working" until the job finishes or actually dies.
 */
const InvoiceUpdateJobWatcher = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const sessionId = sessionContext?.sessionId ?? null;
    const shownToastIds = useRef<Set<string>>(new Set());
    const pollingIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!sessionId) return;

        const toastKey = (job: TrackedInvoiceUpdate) => job.jobId ?? `${job.mode}:${job.invoiceId}`;

        const showProcessingToast = (job: TrackedInvoiceUpdate) => {
            const id = `invoice-update-${toastKey(job)}`;
            if (shownToastIds.current.has(id) || toast.isActive(id)) return;
            shownToastIds.current.add(id);
            const label = job.invoiceNumber ? `Invoice ${job.invoiceNumber}` : 'This invoice';
            toast({
                id,
                title: processingTitle(job.kind),
                description: `${label} is still processing. You can leave this page.`,
                status: 'info',
                duration: null,
                isClosable: true,
            });
        };

        const closeProcessingToast = (job: TrackedInvoiceUpdate) => {
            const id = `invoice-update-${toastKey(job)}`;
            if (toast.isActive(id)) toast.close(id);
            shownToastIds.current.delete(id);
        };

        const showResultToast = (job: TrackedInvoiceUpdate, result: {
            status: 'succeeded' | 'failed' | 'unknown';
            invoiceStatus?: string;
            nexRefreshWarning?: { title?: string; detail?: string };
            errorMessage?: string;
        }) => {
            if (result.status === 'unknown') {
                toast({
                    title: 'Still processing',
                    description: 'Check the invoice. A timeout does not mean the save failed.',
                    status: 'info',
                    duration: 8000,
                    isClosable: true,
                });
                return;
            }
            if (result.status === 'failed') {
                toast({
                    title: 'Failed to update invoice',
                    description: result.errorMessage ?? 'The invoice update did not finish. Please try again.',
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
            if (result.nexRefreshWarning && job.kind === 'refresh') {
                toast({
                    title: result.nexRefreshWarning.title ?? 'Nexsyis refresh incomplete',
                    description: result.nexRefreshWarning.detail ?? 'Could not reload all purchase-order data from Nexsyis. Existing matches were kept.',
                    status: 'warning',
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
            if (job.kind === 'submit') {
                const submitted = result.invoiceStatus === 'manually_submitted' || result.invoiceStatus === 'submitted';
                toast({
                    title: submitted ? 'Invoice submitted' : 'Submit did not complete',
                    description: submitted
                        ? 'The invoice has been marked as manually submitted.'
                        : 'The invoice is still flagged. Resolve remaining issues or check for a NEX error, then try again.',
                    status: submitted ? 'success' : 'warning',
                    duration: submitted ? 4000 : 6000,
                    isClosable: true,
                });
                return;
            }
            toast({
                title: job.kind === 'refresh' ? 'Nexsyis data refreshed' : 'Invoice updated',
                description: job.kind === 'refresh'
                    ? 'Purchase-order data was reloaded from Nexsyis and reconciliation was updated.'
                    : 'Your changes have been saved.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        };

        const settle = async (
            job: TrackedInvoiceUpdate,
            result: {
                status: 'succeeded' | 'failed' | 'unknown';
                invoiceStatus?: string;
                nexRefreshWarning?: { title?: string; detail?: string; httpStatus?: number };
                errorMessage?: string;
            },
        ) => {
            try {
                await queryClient.refetchQueries({ queryKey: ['/invoice/detail', job.invoiceId] });
            } catch {
                // The toast still reports the job outcome if the refetch fails.
            }
            queryClient.invalidateQueries({ queryKey: ['/invoice/activity-logs', job.invoiceId] });
            queryClient.invalidateQueries({ queryKey: ['/invoices/feed'] });
            finishInvoiceUpdate(
                {
                    invoiceId: job.invoiceId,
                    status: result.status,
                    kind: job.kind,
                    input: job.input,
                    savedFields: job.savedFields,
                    invoiceStatus: result.invoiceStatus,
                    nexRefreshWarning: result.nexRefreshWarning,
                    errorMessage: result.errorMessage,
                },
                { jobId: job.jobId, invoiceId: job.invoiceId, mode: job.mode },
            );
            closeProcessingToast(job);
            showResultToast(job, result);
        };

        const pollJob = async (job: TrackedInvoiceUpdate) => {
            if (!job.jobId) return;
            const { data } = await axios.post<JobStatusResponse>(
                `${API_URL}/invoice/update-status`,
                { jobId: job.jobId },
                { headers: { Authorization: sessionId } },
            );
            if (data.status === 'processing') return;
            if (data.status === 'failed' && data.error?.timedOut) {
                await settle(job, { status: 'unknown' });
                return;
            }
            await settle(job, {
                status: data.status === 'succeeded' ? 'succeeded' : 'failed',
                invoiceStatus: data.invoiceStatus,
                nexRefreshWarning: data.nexRefreshWarning,
                errorMessage: data.error?.detail ?? data.error?.message ?? data.error?.title,
            });
        };

        const pollCompletion = async (job: TrackedInvoiceUpdate) => {
            const { data } = await axios.post<InvoiceDetailResponse>(
                `${API_URL}/invoice/detail`,
                { invoiceId: job.invoiceId },
                { headers: { Authorization: sessionId } },
            );
            const beforeMs = updateCompletedAtMs(job.completedAtBefore);
            const afterMs = updateCompletedAtMs(data.updateCompletedAt);
            const saved = afterMs != null && (beforeMs == null || afterMs > beforeMs);
            if (saved) {
                queryClient.setQueryData(['/invoice/detail', job.invoiceId], data);
                await settle(job, {
                    status: 'succeeded',
                    invoiceStatus: data.status,
                    nexRefreshWarning: undefined,
                });
                return;
            }
            if (Date.now() - job.startedAt >= COMPLETION_WAIT_MS) {
                await settle(job, { status: 'unknown' });
            }
        };

        const tick = async () => {
            const current = listInvoiceUpdates();
            current.forEach(showProcessingToast);
            await Promise.all(current.map(async (job) => {
                const key = toastKey(job);
                if (pollingIds.current.has(key)) return;
                pollingIds.current.add(key);
                try {
                    if (job.mode === 'job') await pollJob(job);
                    else await pollCompletion(job);
                } catch (err) {
                    console.error('[INVOICE_UPDATE_POLL]', err);
                    if (Date.now() - job.startedAt >= COMPLETION_WAIT_MS + 20_000) {
                        await settle(job, { status: 'unknown' });
                    }
                } finally {
                    pollingIds.current.delete(key);
                }
            }));
        };

        const unsubscribe = subscribeInvoiceUpdateJobs(() => {
            void tick();
        });
        void tick();
        const timer = window.setInterval(() => {
            void tick();
        }, POLL_MS);
        return () => {
            unsubscribe();
            window.clearInterval(timer);
        };
    }, [queryClient, sessionId, toast]);

    return null;
};

export default InvoiceUpdateJobWatcher;
